import { compact, concat, filter, flow, includes, isEmpty, isFunction, isNil, map } from 'lodash/fp';
import { Fragment, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { H1, LoadingPageContent, P, Wrapper } from '@ahmdigital/ui';
import { PRODUCT_TYPE } from '@ahmdigital/constants';
import { useDispatch, useSelector } from 'react-redux';
import getParentProductId from '@ahmdigital/logic/lib/utils/get-parent-product-id';

import { DisclaimerWrapper, HeaderWrapper, StyledButton, StyledMoreOptionsLink } from './styles';
import { DRAWER_IDS } from '../../../../ahm-constants/ui';
import { EVENTS } from '../../../../analytics/google/constants';
import { EXTRA_OPTIONS_PROPS, HOSPITAL_OPTIONS_PROPS } from './components/add-ons/illustrations/constants';
import { EXTRAS_COPY, HOSPITAL_COPY } from './components/add-ons/constants';
import { ProductType } from '../../../../types/product';
import { URLS } from '../../../../models/url/constants';
import { useByocNavigationContext } from '../../context/byoc-navigation-context';
import AddOns from './components/add-ons';
import analytics from '../../../../analytics';
import browserUtils from '../../../../utils/browser';
import buildYourCoverActions from '../../../../actions/build-your-cover';
import CarouselSection from './components/carousel-section';
import CustomerActions from '../../../../actions/customer';
import DrawerActions from '../../../../actions/drawer';
import getBestSeller from '../../utils/get-best-seller';
import getCarouselTypeOrder from '../../utils/get-carousel-type-order';
import getCustomerHasChanged from '../../../../utils/customer-has-changed';
import getFutureDate from '../../../../utils/get-future-date';
import getSelector from '../../utils/get-selector';
import getUnavailableProductProps from '../../utils/get-unavailable-product-props';
import PriceActions from '../../../../actions/price';
import PriceDisclaimer from '../../../../components/price-disclaimer';
import PricingDrawer from '../../../../components/pricing-drawer';
import RedirectAdapter from '../../../../utils/redirect-adapter';
import refineProductsByParentId from '../../utils/refine-products-by-parent-id';
import SalesIllustrations from './components/add-ons/illustrations';
import selectBuildYourCover from '../../../../selectors/select-build-your-cover';
import selectCustomer from '../../../../selectors/select-customer';
import selectEffectiveDateOverride from '../../../../selectors/select-effective-date-override';
import selectPrices from '../../../../selectors/select-prices';
import selectProducts from '../../../../selectors/select-products';
import StepFooterNavigation from '../../components/step-footer-navigation';
import useGetSuitableProductIds from '../../utils/use-get-suitable-products';
import ValidationAlert from './components/validation-alert';

const { EXTRAS, HOSPITAL, PACKAGE } = PRODUCT_TYPE;

const ChooseYourCover = () => {
  const dispatch = useDispatch();
  const windowService = browserUtils.getWindow();

  const prices = useSelector(selectPrices);
  const futureEffectiveDate = useSelector(selectEffectiveDateOverride);

  const { journeyType } = useByocNavigationContext();

  const customer = useSelector(selectCustomer);
  const { hospitalExcess } = customer;
  const customerHasChanged = getCustomerHasChanged(customer, Boolean(futureEffectiveDate));

  const products = useSelector(selectProducts);

  const { addOnType, selectedProducts, selectedServices } = useSelector(selectBuildYourCover);

  const hospitalSectionRef = useRef<HTMLDivElement>(null);
  const extrasSectionRef = useRef<HTMLDivElement>(null);

  const [carouselStuck, setCarouselStuck] = useState<string | null>(null);

  const hasScrolled = useRef(false);

  const [hospitalProducts, setHospitalProducts] = useState<ProductType[]>([]);

  const {
    extrasProductIds,
    hasExtrasWithFewerInclusions,
    hasHospitalWithFewerInclusions,
    hasError,
    isLoading,
    hospitalParentProductIds,
  }: {
    extrasProductIds: ProductType['id'][];
    hasExtrasWithFewerInclusions: boolean;
    hasHospitalWithFewerInclusions: boolean;
    hasError: boolean;
    isLoading: boolean;
    hospitalParentProductIds: ProductType['parentProductId'][];
  } = useGetSuitableProductIds({
    extrasServices: selectedServices.extras,
    hospitalServices: selectedServices.hospital,
    scale: customer.scale,
  });

  const extrasBestSeller = getBestSeller({ productType: PRODUCT_TYPE.EXTRAS, scale: customer.scale });
  const hasExtrasBestSellerWithFewerInclusions =
    hasExtrasWithFewerInclusions && includes(extrasBestSeller, extrasProductIds);

  const hospitalBestSeller = getBestSeller({ productType: PRODUCT_TYPE.HOSPITAL, scale: customer.scale });
  const hasHospitalBestSellerWithFewerInclusions =
    hasHospitalWithFewerInclusions && includes(hospitalBestSeller, hospitalParentProductIds);

  const hasBestSellerWithFewerInclusions =
    hasExtrasBestSellerWithFewerInclusions || hasHospitalBestSellerWithFewerInclusions;

  const extrasProducts = useSelector((state) => getSelector(extrasProductIds, state)) as ProductType[];

  const carouselTypeOrder = getCarouselTypeOrder(addOnType);

  // this returns the actual products that match the product ids
  const allHospitalProducts: ProductType[] = flow(
    filter((product: ProductType) => includes(product.parentProductId, hospitalParentProductIds)),
    map((product) => ({
      ...product,
      unavailableWithExcessInfo: getUnavailableProductProps(
        CustomerActions.changeHospitalExcess,
        hospitalExcess,
        product,
        dispatch,
      ),
    })),
    compact,
  )(products);

  const refinedProducts = refineProductsByParentId(hospitalParentProductIds, allHospitalProducts);
  const productsAvailableByScale = filter(
    (product: ProductType) => includes(customer.scale, product.scales?.supported),
    refinedProducts,
  ) as ProductType[];

  const stringedExtrasHospitalAndParentIds = JSON.stringify([
    extrasProducts,
    hospitalProducts,
    hospitalParentProductIds,
  ]);

  useEffect(() => {
    setHospitalProducts(productsAvailableByScale);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerHasChanged, hospitalExcess, stringedExtrasHospitalAndParentIds]);

  const productIds = map('id', concat(extrasProducts, hospitalProducts));

  useEffect(() => {
    if (!isEmpty(hospitalProducts)) {
      dispatch(PriceActions.load(productIds, customer, { date: getFutureDate(Boolean(futureEffectiveDate)) }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerHasChanged, hospitalExcess, futureEffectiveDate, JSON.stringify([extrasProducts, hospitalProducts])]);

  const { hospital: selectedHospitalProductId } = selectedProducts;
  const parentProductId = selectedHospitalProductId ? getParentProductId(selectedHospitalProductId) : null;

  useEffect(() => {
    if (!selectedHospitalProductId || !parentProductId) return;
    const excessHasUpdated = !includes(`-${hospitalExcess}`, selectedHospitalProductId);

    if (!excessHasUpdated) return;

    const validHospitalProductIds = map('id', products);

    const updatedHospitalProduct = `${parentProductId}-excess-${hospitalExcess}`;

    const productToSetOrUnset = includes(updatedHospitalProduct, validHospitalProductIds)
      ? updatedHospitalProduct
      : selectedHospitalProductId;

    dispatch(buildYourCoverActions.setOrUnsetSelectedProduct('hospital', productToSetOrUnset));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hospitalExcess]);

  const stringedHospitalAndExtrasProducts = JSON.stringify([extrasProducts, hospitalProducts, addOnType]);

  useEffect(() => {
    if (!(isEmpty(extrasProducts) || isEmpty(hospitalProducts) || isEmpty(prices))) {
      analytics.ecommerceGA4(EVENTS.ECOMMERCE_GA4.VIEW_ITEM_LIST, {
        customer,
        prices,
        products: carouselTypeOrder.flatMap((type) => (type === HOSPITAL ? hospitalProducts : extrasProducts)),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stringedHospitalAndExtrasProducts, prices]);

  const handleScroll = () => {
    const firstHospitalProduct = document.getElementById(hospitalProducts?.[0]?.id ?? '');
    const hospitalHeadTop = firstHospitalProduct?.getBoundingClientRect()?.top;

    const firstExtrasProduct = document.getElementById(extrasProducts?.[0]?.id ?? '');
    const extrasHeadTop = firstExtrasProduct?.getBoundingClientRect()?.top;

    if (!isNil(hospitalHeadTop) || !isNil(extrasHeadTop)) {
      if (hospitalHeadTop === 0) {
        setCarouselStuck(HOSPITAL);
      } else if (extrasHeadTop === 0) {
        setCarouselStuck(EXTRAS);
      } else {
        setCarouselStuck(null);
      }
    }
  };

  useLayoutEffect(() => {
    if (
      carouselTypeOrder.length < 1 ||
      journeyType !== PACKAGE ||
      hasScrolled.current ||
      addOnType === null ||
      isEmpty(hospitalProducts) ||
      isEmpty(extrasProducts)
    )
      return;

    const targetRef = carouselTypeOrder.at(-1) === EXTRAS ? extrasSectionRef : hospitalSectionRef;
    targetRef.current?.scrollIntoView({ behavior: 'smooth' });
    hasScrolled.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [journeyType, hospitalProducts, extrasProducts, carouselTypeOrder]);

  useEffect(() => {
    if ('addEventListener' in windowService && isFunction(windowService.addEventListener)) {
      windowService.addEventListener('scroll', handleScroll);
    }

    return () => {
      if ('removeEventListener' in windowService && isFunction(windowService.removeEventListener)) {
        windowService.removeEventListener('scroll', handleScroll);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productIds]);

  const openChangeDetailsDrawer = useCallback(() => {
    DrawerActions.openDrawer(DRAWER_IDS.CHANGE_DETAILS)(dispatch);
  }, [dispatch]);

  const hospitalCarousel = journeyType !== EXTRAS && (
    <>
      <CarouselSection
        hasProductWithFewerInclusions={hasHospitalWithFewerInclusions}
        productType={HOSPITAL}
        products={hospitalProducts}
        isStuck={carouselStuck === HOSPITAL}
        sectionRef={hospitalSectionRef}
      />
      {journeyType === HOSPITAL && (
        <AddOns
          title="Extras cover"
          link={{
            label: 'Add extras',
            url: URLS.SALES.CHOOSE_YOUR_COVER_COMBINED_EXTRAS_SERVICES,
          }}
          description={EXTRAS_COPY}
          illustrationsBox={<SalesIllustrations {...EXTRA_OPTIONS_PROPS} />}
          productType={EXTRAS}
        />
      )}
    </>
  );

  const extrasCarousel = journeyType !== HOSPITAL && (
    <>
      <CarouselSection
        hasProductWithFewerInclusions={hasExtrasWithFewerInclusions}
        productType={EXTRAS}
        products={extrasProducts}
        isStuck={carouselStuck === EXTRAS}
        sectionRef={extrasSectionRef}
      />
      {journeyType === EXTRAS && (
        <AddOns
          title="Hospital cover"
          link={{
            label: 'Add hospital',
            url: URLS.SALES.CHOOSE_YOUR_COVER_COMBINED_HOSPITAL_SERVICES,
          }}
          description={HOSPITAL_COPY}
          illustrationsBox={<SalesIllustrations {...HOSPITAL_OPTIONS_PROPS} />}
          productType={HOSPITAL}
        />
      )}
    </>
  );

  const carouselElements = {
    [EXTRAS]: extrasCarousel,
    [HOSPITAL]: hospitalCarousel,
  };

  if (hasError) {
    return <RedirectAdapter to={URLS.SALES.ERROR_500} />;
  }

  if (isLoading) {
    return <LoadingPageContent />;
  }

  return (
    <>
      <Wrapper size="large">
        <HeaderWrapper>
          <H1 looksLike="h2">Choose the cover that suits you</H1>

          <P variant="withoutMargin">Here are options we think you’ll like based on the inclusions you chose</P>
          {hasBestSellerWithFewerInclusions && <P>There’s also a best-seller that’s worth a look.</P>}
        </HeaderWrapper>

        <DisclaimerWrapper>
          <PriceDisclaimer
            bold
            elementType="button"
            forProducts={hospitalProducts}
            highlightIncome
            showRefineLink={false}
          />
          <StyledButton
            icon={{ name: 'pencil' }}
            onClick={openChangeDetailsDrawer}
            variant={['border', 'white', 'smallPadding']}
          >
            Edit
          </StyledButton>
        </DisclaimerWrapper>

        <ValidationAlert
          hospitalSectionRef={hospitalSectionRef}
          extrasSectionRef={extrasSectionRef}
          addOnType={addOnType}
        />
        {carouselTypeOrder.map((type) => (
          <Fragment key={type}>{carouselElements[type]}</Fragment>
        ))}
      </Wrapper>
      <StyledMoreOptionsLink />
      <StepFooterNavigation />

      <PricingDrawer isProductWithHospitalServices showIncomeOptions />
    </>
  );
};

export default ChooseYourCover;
